/**
 * Error Context Enrichment
 *
 * Provides standardized context for all error tracking events.
 * Ensures consistent metadata across frontend errors.
 */

// App version - update with releases
const APP_VERSION = '1.0.0';

// Session ID persists for the browser session
let sessionId: string | null = null;

function getSessionId(): string {
  if (!sessionId) {
    // Try to get from sessionStorage first
    sessionId = sessionStorage.getItem('error_session_id');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('error_session_id', sessionId);
    }
  }
  return sessionId;
}

/**
 * Generate a unique request ID for tracing across frontend and backend
 */
export function generateRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Get browser information
 */
function getBrowserInfo(): string {
  if (typeof navigator === 'undefined') return 'unknown';

  const ua = navigator.userAgent;
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return 'Other';
}

/**
 * Get OS information
 */
function getOSInfo(): string {
  if (typeof navigator === 'undefined') return 'unknown';

  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Android')) return 'Android';
  return 'Other';
}

/**
 * Get current route name from URL
 */
function getCurrentRouteName(): string {
  if (typeof window === 'undefined') return 'unknown';

  const path = window.location.pathname;
  // Remove leading slash and get first segment
  const segments = path.split('/').filter(Boolean);
  return segments[0] || 'home';
}

/**
 * Standard error context interface
 */
export interface ErrorContext {
  // Session context
  session_id: string;
  request_id?: string;

  // Environment context
  environment: string;
  app_version: string;
  browser: string;
  os: string;

  // Page context
  current_url: string;
  referrer: string;
  route_name: string;

  // Timing context
  timestamp: string;
}

/**
 * User context to be merged with error context
 * (kept separate since it comes from auth state)
 */
export interface UserErrorContext {
  user_id?: string;
  user_email?: string;
  is_advisor?: boolean;
  subscription_tier?: string;
}

/**
 * Get base error context (without user info)
 * User info should be added by the caller if available
 */
export function getErrorContext(requestId?: string): ErrorContext {
  const isProduction = typeof window !== 'undefined' &&
    window.location.hostname !== 'localhost' &&
    !window.location.hostname.includes('127.0.0.1');

  return {
    session_id: getSessionId(),
    request_id: requestId,
    environment: isProduction ? 'production' : 'development',
    app_version: APP_VERSION,
    browser: getBrowserInfo(),
    os: getOSInfo(),
    current_url: typeof window !== 'undefined' ? window.location.href : 'unknown',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    route_name: getCurrentRouteName(),
    timestamp: new Date().toISOString(),
  };
}

/**
 * Get full error context with user information
 */
export function getFullErrorContext(
  user?: { id?: string; email?: string },
  profile?: { is_advisor?: boolean; subscription_tier?: string },
  requestId?: string
): ErrorContext & UserErrorContext {
  return {
    ...getErrorContext(requestId),
    user_id: user?.id,
    user_email: user?.email,
    is_advisor: profile?.is_advisor,
    subscription_tier: profile?.subscription_tier,
  };
}

/**
 * Create an error fingerprint for grouping similar errors
 * Uses error message + source to create a consistent hash
 */
export function createErrorFingerprint(
  errorMessage: string,
  source: string
): string {
  // Normalize the message: remove specific IDs, timestamps, etc.
  const normalized = errorMessage
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[UUID]')
    .replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/g, '[TIMESTAMP]')
    .replace(/\d+/g, '[NUM]')
    .substring(0, 100);

  // Simple hash function
  let hash = 0;
  const str = `${source}:${normalized}`;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `err_${Math.abs(hash).toString(16)}`;
}

/**
 * Sanitize error data to remove potential PII
 */
export function sanitizeErrorData(data: Record<string, unknown>): Record<string, unknown> {
  const sensitiveKeys = ['password', 'token', 'secret', 'api_key', 'apikey', 'authorization', 'cookie', 'ssn', 'credit_card'];

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();

    // Check if key contains sensitive terms
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string' && value.length > 500) {
      // Truncate long strings
      sanitized[key] = value.substring(0, 500) + '...[truncated]';
    } else if (typeof value === 'object' && value !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeErrorData(value as Record<string, unknown>);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}
